/**
 * Translates between a grid's column keys and the `sortBy` its API accepts,
 * for the grids where the two differ (`createdAt` vs `created_at`, `status` vs
 * `status_id`).
 *
 * Both directions are needed and both come from the same map, so they cannot
 * drift apart:
 *
 *  - `toBackend` turns a clicked column into a request parameter. It returns
 *    undefined for an unmapped column rather than a default -- these mappers
 *    used to fall back to `created_at`, which re-sorted the grid by a column
 *    nobody clicked while the header arrow claimed otherwise.
 *  - `toColumn` turns the active request parameter back into a column key, so
 *    the header arrow can be driven by the sort actually in effect. Without it
 *    the arrow is a separate piece of state that starts out disagreeing with
 *    the query and stays wrong for a shared `?sortBy=` link.
 */
export interface SortMapper<T extends Record<string, string>> {
  toBackend: (columnKey: string) => T[keyof T] | undefined;
  toColumn: (backendKey: string | undefined) => string | undefined;
}

export function createSortMapper<T extends Record<string, string>>(mapping: T): SortMapper<T> {
  const byBackendKey = new Map<string, string>();
  for (const [columnKey, backendKey] of Object.entries(mapping)) {
    // First column wins if two map to the same backend key, so the inverse is
    // deterministic rather than dependent on object order.
    if (!byBackendKey.has(backendKey)) byBackendKey.set(backendKey, columnKey);
  }

  return {
    toBackend: (columnKey) => mapping[columnKey] as T[keyof T] | undefined,
    toColumn: (backendKey) => (backendKey ? byBackendKey.get(backendKey) : undefined),
  };
}

/**
 * The grids whose column keys differ from their `sortBy` values, kept here
 * rather than inside each page so both directions of the translation are
 * reachable from a test -- importing a page to reach its map would drag in the
 * whole page's module graph.
 *
 * Column key on the left (what the header sends), request parameter on the
 * right (what the API accepts).
 */
export const PAYMENTS_SORT_MAP = {
  external_reference: 'external_reference',
  direction: 'direction',
  payer_party: 'payer_party',
  payee_party: 'payee_party',
  amount_total: 'amount_total',
  status: 'status_id',
  execution_date_requested: 'execution_date_requested',
  created_at: 'created_at',
} as const satisfies Record<string, string>;

/** Beneficiaries grid. `name` orders on the party's first name server-side. */
export const SUPPLIERS_SORT_MAP = {
  name: 'first_name',
  email: 'email',
  createdAt: 'created_at',
  createdByName: 'created_by_name',
  relationshipApproval: 'relationship_approval',
  externalRef: 'external_ref',
} as const satisfies Record<string, string>;

/** Opportunities grid. */
export const PROSPECT_COMPANIES_SORT_MAP = {
  name: 'name',
  status: 'status',
  createdAt: 'created_at',
} as const satisfies Record<string, string>;
